import { TrilingualInput } from 'src/app/components/trilingual-input/trilingual-input.component';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggingService } from './logging.service';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { FormNotifications } from '../templates/notifications-component/notifications.component';
import { UserSearch } from '../components/cognito/cognito.component';
import { ConfigurationService } from '../pages/offline/configuration.service';
import { DataService } from '../pages/offline/data-service';
import { FileToDelete, FileToUpload } from '../pages/offline/databases/data-db';
import { News } from '../pages/news-list/news/news.component';
import SearchParams from '../components/free-text-search/models/search-params.model';
import Contact from '../pages/contacts/models/contact.model';
import { BehaviorSubject } from 'rxjs';
import { Property } from '../pages/properties/models/property.model';
import { CurrentProposition } from '../pages/maps/maps-proposition/map-proposition.interface';
import { AmplifyApiService } from './amplify-api.service';
import { v4 as uuidv4 } from 'uuid';

export type AmplifyUser = {
  username: string;
  attributes: {
    sub?: string;
    email_verified?: string;
    nickname?: string;
    phone_number_verified?: string;
    phone_number?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    picture?: string;
  };
};

export interface FormDefinitionFullTextResult {
  id: string;
  name: string;
  descriptor: {
    title: string;
    title_es: string;
    title_fr: string;
  };
}

export interface VisualControl {
  _id: string;
  name: string;
  description: string;
  lastModified: number;
  image?: string;
  imagePath?: string;
}

export interface Unit {
  _id: string;
  name: string;
  description: string;
  description_es: string;
  description_fr: string;
  lastModified: number;
}

export interface Validator {
  name: string;
  checked: boolean;
  value?: any;
  message: string;
  message_es: string;
  message_fr: string;
  status: Array<string>;
}

export interface Validations {
  required: Validator;
  email: Validator;
  minLength: Validator;
  maxLength: Validator;
  min: Validator;
  max: Validator;
  pattern: Validator;
  custom: Validator;
}

export interface AvailableValue {
  value: string;
  label: string;
  label_es: string;
  label_fr: string;
}

export interface AvailableValues {
  type: string;
  listName?: string;
  countryField?: string;
  values?: AvailableValue[];
}

export interface Field {
  name: string;
  label: string;
  label_es: string;
  label_fr: string;
  tooltipTitle?: string;
  tooltipTitle_es?: string;
  tooltipTitle_fr?: string;
  defaultValue?: any;
  defaultValue_es?: any;
  defaultValue_fr?: any;
  dataType: string;
  controlType: string;
  stationVisible: boolean;
  exploitationType: string;
  shapeType?: string;
  controlSubType?: string;
  reference?: string;
  referenceProgramAware: boolean;
  referenceFilter: string;
  referenceFilterValue: string;
  referenceFilter2: string;
  referenceFilterValue2: string;
  allowLoadAll: boolean;
  units?: string;
  showUnitsLabel: boolean;
  showUnitsValue: boolean;
  hasFeedback: boolean;
  readonly: boolean;
  repeatable: boolean;
  placeholder: string;
  placeholder_es: string;
  placeholder_fr: string;
  prefixIcon?: string;
  otherField: boolean;
  hasFilter: boolean;
  hasShapeAssociationFilter: boolean;
  subGroupName: string;
  onlyOnOtherValueField: boolean;
  customComponent?: string;
  validations: Validations;
  ngModelChange?: string;
  stationCreatedMethod: string;
  step?: number;
  precision?: number;
  formatter: string;
  parser: string;
  availableValues?: AvailableValues;
  inline?: boolean;
  showTime?: boolean;
  disabledDates?: string;
  disabledTimes?: string;
  controlMode?: string;
  minRows?: number;
  maxRows?: number;
  class?: string;
  order: number;
  selectableNodes?: Array<{ label: string; value: string; checked: boolean }>;
  inputDataLabelColumn1?: TrilingualInput;
  inputDataLabelColumn2?: TrilingualInput;
  inputDataLabelColumn3?: TrilingualInput;
  inputDataLabelColumn4?: TrilingualInput;
  inputDataLabelColumn5?: TrilingualInput;
  inputDataProperty: string;
  inputDataHideCalculationsTable: boolean;
  inputDataTableCostsReadonly: boolean;
  isDescriptive: boolean;
  uploadMimeTypes?: string[];
  textDisplay: string;
  textDisplayHTML: string;
  customActionMethodName: string;
  customActionGlobal: boolean;
  hasCreateNew: boolean;
  fieldsToShow: Array<string>;
  mandatoryFields: Array<string>;
  contactType: string;
  excludedContactTypes: Array<string>;
  allowContactSearch: boolean;
  shapeAssociationMaster: boolean;
  shapeAssociationDescriptiveField: string;
}

export interface Group {
  name: string;
  label?: string;
  label_es?: string;
  label_fr?: string;
  repeatable: boolean;
  atLeastOneRepetitionNeeded: boolean;
  defaultOpen: boolean;
  closeable: boolean;
  statusVisibility: Array<string>;
  statusEditable: Array<string>;
  statusOpen: Array<string>;
  onAddGroupMethod: string;
  onRemoveGroupMethod: string;
  isSeparator: boolean;
  fields?: Field[];
  order: number;
  isSubGroup: boolean;
  closedRepeatableGroupLabel: TrilingualInput;
}

export interface FormDefinition {
  _id: string;
  name: string;
  type: string;
  allowPrint: boolean;
  printContactSummary: boolean;
  allowSendToOwner: boolean;
  useFormStatus: boolean;
  useFormStatus1: boolean;
  useFormStatus2: boolean;
  useFormStatus3: boolean;
  useFormStatus4: boolean;
  useFormStatus5: boolean;
  useFormStatus6: boolean;
  useFormStatus7: boolean;
  useFormStatus8: boolean;
  useFormStatus9: boolean;
  useFormStatus10: boolean;
  isFinalStatus2: boolean;
  isFinalStatus3: boolean;
  isFinalStatus4: boolean;
  isFinalStatus5: boolean;
  isFinalStatus6: boolean;
  isFinalStatus7: boolean;
  isFinalStatus8: boolean;
  isFinalStatus9: boolean;
  isFinalStatus10: boolean;
  formStatus1Labels: any;
  formStatus2Labels: any;
  formStatus3Labels: any;
  formStatus4Labels: any;
  formStatus5Labels: any;
  formStatus6Labels: any;
  formStatus7Labels: any;
  formStatus8Labels: any;
  formStatus9Labels: any;
  formStatus10Labels: any;
  formStatus1Action2Labels: any;
  formStatus1Action3Labels: any;
  formStatus1Action4Labels: any;
  formStatus1Action5Labels: any;
  formStatus1Action6Labels: any;
  formStatus1Action7Labels: any;
  formStatus1Action8Labels: any;
  formStatus1Action9Labels: any;
  formStatus1Action10Labels: any;
  formStatus2Action1Labels: any;
  formStatus2Action3Labels: any;
  formStatus2Action4Labels: any;
  formStatus2Action5Labels: any;
  formStatus2Action6Labels: any;
  formStatus2Action7Labels: any;
  formStatus2Action8Labels: any;
  formStatus2Action9Labels: any;
  formStatus2Action10Labels: any;
  formStatus3Action1Labels: any;
  formStatus3Action2Labels: any;
  formStatus3Action4Labels: any;
  formStatus3Action5Labels: any;
  formStatus3Action6Labels: any;
  formStatus3Action7Labels: any;
  formStatus3Action8Labels: any;
  formStatus3Action9Labels: any;
  formStatus3Action10Labels: any;
  formStatus4Action1Labels: any;
  formStatus4Action2Labels: any;
  formStatus4Action3Labels: any;
  formStatus4Action5Labels: any;
  formStatus4Action6Labels: any;
  formStatus4Action7Labels: any;
  formStatus4Action8Labels: any;
  formStatus4Action9Labels: any;
  formStatus4Action10Labels: any;
  formStatus5Action1Labels: any;
  formStatus5Action2Labels: any;
  formStatus5Action3Labels: any;
  formStatus5Action4Labels: any;
  formStatus5Action6Labels: any;
  formStatus5Action7Labels: any;
  formStatus5Action8Labels: any;
  formStatus5Action9Labels: any;
  formStatus5Action10Labels: any;
  formStatus6Action1Labels: any;
  formStatus6Action2Labels: any;
  formStatus6Action3Labels: any;
  formStatus6Action4Labels: any;
  formStatus6Action5Labels: any;
  formStatus6Action7Labels: any;
  formStatus6Action8Labels: any;
  formStatus6Action9Labels: any;
  formStatus6Action10Labels: any;
  formStatus7Action1Labels: any;
  formStatus7Action2Labels: any;
  formStatus7Action3Labels: any;
  formStatus7Action4Labels: any;
  formStatus7Action5Labels: any;
  formStatus7Action6Labels: any;
  formStatus7Action8Labels: any;
  formStatus7Action9Labels: any;
  formStatus7Action10Labels: any;
  formStatus8Action1Labels: any;
  formStatus8Action2Labels: any;
  formStatus8Action3Labels: any;
  formStatus8Action4Labels: any;
  formStatus8Action5Labels: any;
  formStatus8Action6Labels: any;
  formStatus8Action7Labels: any;
  formStatus8Action9Labels: any;
  formStatus8Action10Labels: any;
  formStatus9Action1Labels: any;
  formStatus9Action2Labels: any;
  formStatus9Action3Labels: any;
  formStatus9Action4Labels: any;
  formStatus9Action5Labels: any;
  formStatus9Action6Labels: any;
  formStatus9Action7Labels: any;
  formStatus9Action8Labels: any;
  formStatus9Action10Labels: any;
  formStatus10Action1Labels: any;
  formStatus10Action2Labels: any;
  formStatus10Action3Labels: any;
  formStatus10Action4Labels: any;
  formStatus10Action5Labels: any;
  formStatus10Action6Labels: any;
  formStatus10Action7Labels: any;
  formStatus10Action8Labels: any;
  formStatus10Action9Labels: any;
  formStatus1AllowRuntimeUsers2: any;
  formStatus1AllowRuntimeUsers3: any;
  formStatus1AllowRuntimeUsers4: any;
  formStatus1AllowRuntimeUsers5: any;
  formStatus1AllowRuntimeUsers6: any;
  formStatus1AllowRuntimeUsers7: any;
  formStatus1AllowRuntimeUsers8: any;
  formStatus1AllowRuntimeUsers9: any;
  formStatus1AllowRuntimeUsers10: any;
  formStatus2AllowRuntimeUsers1: any;
  formStatus2AllowRuntimeUsers3: any;
  formStatus2AllowRuntimeUsers4: any;
  formStatus2AllowRuntimeUsers5: any;
  formStatus2AllowRuntimeUsers6: any;
  formStatus2AllowRuntimeUsers7: any;
  formStatus2AllowRuntimeUsers8: any;
  formStatus2AllowRuntimeUsers9: any;
  formStatus2AllowRuntimeUsers10: any;
  formStatus3AllowRuntimeUsers1: any;
  formStatus3AllowRuntimeUsers2: any;
  formStatus3AllowRuntimeUsers4: any;
  formStatus3AllowRuntimeUsers5: any;
  formStatus3AllowRuntimeUsers6: any;
  formStatus3AllowRuntimeUsers7: any;
  formStatus3AllowRuntimeUsers8: any;
  formStatus3AllowRuntimeUsers9: any;
  formStatus3AllowRuntimeUsers10: any;
  formStatus4AllowRuntimeUsers1: any;
  formStatus4AllowRuntimeUsers2: any;
  formStatus4AllowRuntimeUsers3: any;
  formStatus4AllowRuntimeUsers5: any;
  formStatus4AllowRuntimeUsers6: any;
  formStatus4AllowRuntimeUsers7: any;
  formStatus4AllowRuntimeUsers8: any;
  formStatus4AllowRuntimeUsers9: any;
  formStatus4AllowRuntimeUsers10: any;
  formStatus5AllowRuntimeUsers1: any;
  formStatus5AllowRuntimeUsers2: any;
  formStatus5AllowRuntimeUsers3: any;
  formStatus5AllowRuntimeUsers4: any;
  formStatus5AllowRuntimeUsers6: any;
  formStatus5AllowRuntimeUsers7: any;
  formStatus5AllowRuntimeUsers8: any;
  formStatus5AllowRuntimeUsers9: any;
  formStatus5AllowRuntimeUsers10: any;
  formStatus6AllowRuntimeUsers1: any;
  formStatus6AllowRuntimeUsers2: any;
  formStatus6AllowRuntimeUsers3: any;
  formStatus6AllowRuntimeUsers4: any;
  formStatus6AllowRuntimeUsers5: any;
  formStatus6AllowRuntimeUsers7: any;
  formStatus6AllowRuntimeUsers8: any;
  formStatus6AllowRuntimeUsers9: any;
  formStatus6AllowRuntimeUsers10: any;
  formStatus7AllowRuntimeUsers1: any;
  formStatus7AllowRuntimeUsers2: any;
  formStatus7AllowRuntimeUsers3: any;
  formStatus7AllowRuntimeUsers4: any;
  formStatus7AllowRuntimeUsers5: any;
  formStatus7AllowRuntimeUsers6: any;
  formStatus7AllowRuntimeUsers8: any;
  formStatus7AllowRuntimeUsers9: any;
  formStatus7AllowRuntimeUsers10: any;
  formStatus8AllowRuntimeUsers1: any;
  formStatus8AllowRuntimeUsers2: any;
  formStatus8AllowRuntimeUsers3: any;
  formStatus8AllowRuntimeUsers4: any;
  formStatus8AllowRuntimeUsers5: any;
  formStatus8AllowRuntimeUsers6: any;
  formStatus8AllowRuntimeUsers7: any;
  formStatus8AllowRuntimeUsers9: any;
  formStatus8AllowRuntimeUsers10: any;
  formStatus9AllowRuntimeUsers1: any;
  formStatus9AllowRuntimeUsers2: any;
  formStatus9AllowRuntimeUsers3: any;
  formStatus9AllowRuntimeUsers4: any;
  formStatus9AllowRuntimeUsers5: any;
  formStatus9AllowRuntimeUsers6: any;
  formStatus9AllowRuntimeUsers7: any;
  formStatus9AllowRuntimeUsers8: any;
  formStatus9AllowRuntimeUsers10: any;
  formStatus10AllowRuntimeUsers1: any;
  formStatus10AllowRuntimeUsers2: any;
  formStatus10AllowRuntimeUsers3: any;
  formStatus10AllowRuntimeUsers4: any;
  formStatus10AllowRuntimeUsers5: any;
  formStatus10AllowRuntimeUsers6: any;
  formStatus10AllowRuntimeUsers7: any;
  formStatus10AllowRuntimeUsers8: any;
  formStatus10AllowRuntimeUsers9: any;

  formStatus1ForceRuntimeUsers2: any;
  formStatus1ForceRuntimeUsers3: any;
  formStatus1ForceRuntimeUsers4: any;
  formStatus1ForceRuntimeUsers5: any;
  formStatus1ForceRuntimeUsers6: any;
  formStatus1ForceRuntimeUsers7: any;
  formStatus1ForceRuntimeUsers8: any;
  formStatus1ForceRuntimeUsers9: any;
  formStatus1ForceRuntimeUsers10: any;
  formStatus2ForceRuntimeUsers1: any;
  formStatus2ForceRuntimeUsers3: any;
  formStatus2ForceRuntimeUsers4: any;
  formStatus2ForceRuntimeUsers5: any;
  formStatus2ForceRuntimeUsers6: any;
  formStatus2ForceRuntimeUsers7: any;
  formStatus2ForceRuntimeUsers8: any;
  formStatus2ForceRuntimeUsers9: any;
  formStatus2ForceRuntimeUsers10: any;
  formStatus3ForceRuntimeUsers1: any;
  formStatus3ForceRuntimeUsers2: any;
  formStatus3ForceRuntimeUsers4: any;
  formStatus3ForceRuntimeUsers5: any;
  formStatus3ForceRuntimeUsers6: any;
  formStatus3ForceRuntimeUsers7: any;
  formStatus3ForceRuntimeUsers8: any;
  formStatus3ForceRuntimeUsers9: any;
  formStatus3ForceRuntimeUsers10: any;
  formStatus4ForceRuntimeUsers1: any;
  formStatus4ForceRuntimeUsers2: any;
  formStatus4ForceRuntimeUsers3: any;
  formStatus4ForceRuntimeUsers5: any;
  formStatus4ForceRuntimeUsers6: any;
  formStatus4ForceRuntimeUsers7: any;
  formStatus4ForceRuntimeUsers8: any;
  formStatus4ForceRuntimeUsers9: any;
  formStatus4ForceRuntimeUsers10: any;
  formStatus5ForceRuntimeUsers1: any;
  formStatus5ForceRuntimeUsers2: any;
  formStatus5ForceRuntimeUsers3: any;
  formStatus5ForceRuntimeUsers4: any;
  formStatus5ForceRuntimeUsers6: any;
  formStatus5ForceRuntimeUsers7: any;
  formStatus5ForceRuntimeUsers8: any;
  formStatus5ForceRuntimeUsers9: any;
  formStatus5ForceRuntimeUsers10: any;
  formStatus6ForceRuntimeUsers1: any;
  formStatus6ForceRuntimeUsers2: any;
  formStatus6ForceRuntimeUsers3: any;
  formStatus6ForceRuntimeUsers4: any;
  formStatus6ForceRuntimeUsers5: any;
  formStatus6ForceRuntimeUsers7: any;
  formStatus6ForceRuntimeUsers8: any;
  formStatus6ForceRuntimeUsers9: any;
  formStatus6ForceRuntimeUsers10: any;
  formStatus7ForceRuntimeUsers1: any;
  formStatus7ForceRuntimeUsers2: any;
  formStatus7ForceRuntimeUsers3: any;
  formStatus7ForceRuntimeUsers4: any;
  formStatus7ForceRuntimeUsers5: any;
  formStatus7ForceRuntimeUsers6: any;
  formStatus7ForceRuntimeUsers8: any;
  formStatus7ForceRuntimeUsers9: any;
  formStatus7ForceRuntimeUsers10: any;
  formStatus8ForceRuntimeUsers1: any;
  formStatus8ForceRuntimeUsers2: any;
  formStatus8ForceRuntimeUsers3: any;
  formStatus8ForceRuntimeUsers4: any;
  formStatus8ForceRuntimeUsers5: any;
  formStatus8ForceRuntimeUsers6: any;
  formStatus8ForceRuntimeUsers7: any;
  formStatus8ForceRuntimeUsers9: any;
  formStatus8ForceRuntimeUsers10: any;
  formStatus9ForceRuntimeUsers1: any;
  formStatus9ForceRuntimeUsers2: any;
  formStatus9ForceRuntimeUsers3: any;
  formStatus9ForceRuntimeUsers4: any;
  formStatus9ForceRuntimeUsers5: any;
  formStatus9ForceRuntimeUsers6: any;
  formStatus9ForceRuntimeUsers7: any;
  formStatus9ForceRuntimeUsers8: any;
  formStatus9ForceRuntimeUsers10: any;
  formStatus10ForceRuntimeUsers1: any;
  formStatus10ForceRuntimeUsers2: any;
  formStatus10ForceRuntimeUsers3: any;
  formStatus10ForceRuntimeUsers4: any;
  formStatus10ForceRuntimeUsers5: any;
  formStatus10ForceRuntimeUsers6: any;
  formStatus10ForceRuntimeUsers7: any;
  formStatus10ForceRuntimeUsers8: any;
  formStatus10ForceRuntimeUsers9: any;
  nextStatuses1: Array<string>;
  nextStatuses2: Array<string>;
  nextStatuses3: Array<string>;
  nextStatuses4: Array<string>;
  nextStatuses5: Array<string>;
  nextStatuses6: Array<string>;
  nextStatuses7: Array<string>;
  nextStatuses8: Array<string>;
  nextStatuses9: Array<string>;
  nextStatuses10: Array<string>;
  submitStatus1Method: string;
  submitStatus2Method: string;
  submitStatus3Method: string;
  submitStatus4Method: string;
  submitStatus5Method: string;
  submitStatus6Method: string;
  submitStatus7Method: string;
  submitStatus8Method: string;
  submitStatus9Method: string;
  submitStatus10Method: string;
  autoGeneratedID: boolean;
  canRestartWorkflow: boolean;
  autoGeneratedIDPrefix?: string;
  description: string;
  description_es: string;
  description_fr: string;
  instructions: any;
  instructionsHTML: string;
  notes: any;
  notesHTML: string;
  descriptorTooltip: string;
  descriptorLabel?: TrilingualInput;
  title: string;
  title_es: string;
  title_fr: string;
  formVisibility: string;
  collectionName: string;
  s3Visibility: string;
  descriptionField: string;
  descriptorReadonly: boolean;
  s3Path: string;
  order: number;
  groups: Group[];
  lastModified?: number;
  targetGroups: [];
  frequency: string;
  initMethod: string;
  beforeSaveMethod: string;
  afterSaveMethod: string;
  createdBy: any;
  created: number;
  notifications: FormNotifications;
  programAware: boolean;
  useOnlyCurrentLocaleForDescriptor: boolean;
}

export interface DomainValues {
  _id: string;
  name: string;
  description: string;
  lastModified?: number;
  createdBy?: any;
  created?: number;
}

export interface DomainValue {
  _id: string;
  id: number;
  label: string;
  label_es: string;
  label_fr: string;
  description: string;
  value: any;
  domainValuesId: string;
  domainValuesName: string;
  lastModified?: number;
  createdBy?: any;
  created?: number;
}

export interface MessageFormInfos {
  form: any;
  formDef?: any;
  descriptor?: any;
  descriptor_locale?: string;
  formDefinitionId: string;
  dynamicFormId: string;
  dynamicFormName: string;
  programName?: string;
}

export interface Message {
  _id: string;
  date: number;
  to: string;
  from: UserSearch;
  subject: string;
  subject_es: string;
  subject_fr: string;
  message: string;
  message_es: string;
  message_fr: string;
  forms?: MessageFormInfos[];
  mapProposition?: {
    propositionId: string;
    shapeId: string;
    comments: string;
  };
  attachments?: any[];
  read: boolean;
  lastModified: number;
  targetGroups: string[];
  users: string[];
  hasRouterLink?: boolean;
  routerLinkText?: TrilingualInput;
  routerLink?: string;
  routerLinkParams?: any;
  canBeReplyTo: boolean;
  owner?: boolean;
}

export interface Association {
  _id?: string;
  parentCollection: string;
  parentId: string;
  childCollection: string;
  childId: string;
  fieldName: string;
  title?: string;
  title_es?: string;
  title_fr?: string;
}

export interface ExistingFormValidation {
  key: string;
  value: string[];
}

export const validations: Validations = {
  required: {
    name: 'required',
    checked: false,
    value: null,
    message: 'This field is required',
    message_es: 'Este campo es obligatorio',
    message_fr: 'Ce champ est obligatoire',
    status: [],
  },
  email: {
    name: 'email',
    checked: false,
    value: null,
    message: 'Value provided is not a valid email',
    message_es: 'El valor proporcionado no es un correo electrónico válido',
    message_fr: "La valeur fournie n'est pas un e-mail valide",
    status: [],
  },
  minLength: {
    name: 'minLength',
    checked: false,
    value: 0,
    message: 'Min length is %1',
    message_es: 'La longitud mínima es %1',
    message_fr: 'La longueur minimale est %1',
    status: [],
  },
  maxLength: {
    name: 'maxLength',
    checked: false,
    value: 0,
    message: 'Max length is %1',
    message_es: 'La longitud maxima es %1',
    message_fr: 'La longueur maximale est %1',
    status: [],
  },
  min: {
    name: 'min',
    checked: false,
    value: 0,
    message: 'Min is %1',
    message_es: 'Min es %1',
    message_fr: 'Min est %1',
    status: [],
  },
  max: {
    name: 'max',
    checked: false,
    value: 0,
    message: 'Max is %1',
    message_es: 'Max es %1',
    message_fr: 'Max est %1',
    status: [],
  },
  pattern: {
    name: 'pattern',
    checked: false,
    value: '',
    message: 'Value provided is invalid',
    message_es: 'El valor proporcionado no es válido',
    message_fr: "La valeur fournie n'est pas valide",
    status: [],
  },
  custom: {
    name: 'custom',
    checked: false,
    value: '',
    message: '',
    message_es: '',
    message_fr: '',
    status: [],
  },
};

@Injectable({
  providedIn: 'root',
})
export class APIService {
  public isExtraSmallScreen = false;
  public isSmallerScreen = false;
  public isSmallScreen = false;
  public isMediumScreen = false;
  public isLargeScreen = false;
  public isVeryLargeScreen = false;
  public isXLScreen = false;
  public user: AmplifyUser | undefined;
  public authStatus!: 'authenticated' | 'configuring' | 'unauthenticated';
  public isMenuCollapse = false;

  public notificationShown = false;
  public controls: VisualControl[] = [];

  public onLine: boolean = navigator.onLine;
  public networkInformation = (navigator as any).connection;

  public userIdentityId = '';
  public callingAPI: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public callingAPI$ = this.callingAPI.asObservable();

  public formDefinitions: Array<FormDefinition> = [];

  constructor(
    private readonly logger: LoggingService,
    private readonly configurationService: ConfigurationService,
    private readonly dataService: DataService,
  ) {}

  private readonly API = inject(AmplifyApiService);

  public async getOnlineOfflineFormCollection({
    collection,
    sort = '_id',
    sortOrder = 'asc',
    filters = [],
    limit = 500,
    skip = 0,
    returnCount = false,
    excludeMetadata = true,
    fieldsToExclude = null,
    verifyAssociations = false,
    locale = '',
  }: {
    collection: string;
    sort?: string;
    sortOrder?: string;
    filters?: Array<any>;
    limit?: number;
    skip?: number;
    returnCount?: boolean;
    excludeMetadata?: boolean;
    fieldsToExclude?: null | string[];
    verifyAssociations?: boolean;
    locale?: string;
  }): Promise<any> {
    if (this.onLine) {
      const items = await this.getCollection({
        collection,
        sort,
        sortOrder,
        filters,
        limit,
        skip,
        returnCount,
        excludeMetadata,
        fieldsToExclude,
        verifyAssociations,
        locale,
      });
      this.dataService.populateDynamicForms(items, collection);
      return items;
    }
    let field = '';
    let value = '';
    if (filters.length) {
      field = filters[0].field;
      value = filters[0].value;
    }
    const formDef = this.formDefinitions.find((x) => x.collectionName === collection);
    if (formDef) {
      return this.dataService.getDynamicFormsByFieldValue(formDef._id, field, value, skip, limit);
    }
    return Promise.resolve([]);
  }
  public getCollection({
    collection,
    sort = '_id',
    sortOrder = 'asc',
    filters = [],
    limit = 500,
    skip = 0,
    returnCount = false,
    excludeMetadata = true,
    fieldsToInclude = null,
    fieldsToExclude = null,
    verifyAssociations = false,
    locale = '',
  }: {
    collection: string;
    sort?: string;
    sortOrder?: string;
    filters?: Array<any>;
    limit?: number;
    skip?: number;
    returnCount?: boolean;
    excludeMetadata?: boolean;
    fieldsToExclude?: null | string[];
    fieldsToInclude?: null | string[];
    verifyAssociations?: boolean;
    locale?: string;
  }): Promise<any> {
    if (!collection) {
      throw new Error('collection mandatory');
    }
    if (limit === 500 && filters.length === 0) {
      this.logger.warn('getCollection called without filter', collection);
    }
    return this.API.get(
      environment.API_NAME,
      `/v2/collections/${collection}?sort=${sort}&sortOrder=${sortOrder}&filters=${encodeURIComponent(
        JSON.stringify(filters),
      )}&limit=${limit}&skip=${skip}&returnCount=${returnCount}&excludeMetadata=${excludeMetadata}&excludedFields=${JSON.stringify(
        fieldsToExclude,
      )}&includedFields=${JSON.stringify(fieldsToInclude)}&verifyAssociations=${verifyAssociations}&locale=${locale}`,
      {},
    );
  }

  public async getOnlineOfflinePrograms(): Promise<any> {
    if (this.onLine) {
      const programs = await this.getCollection({ collection: 'programs' });
      this.dataService.populatePrograms(programs);
      return programs;
    } else {
      return await this.dataService.getPrograms();
    }
  }

  public changeProgram(propertyId: string, programId: string): Promise<any> {
    if (!propertyId || !programId) {
      throw new Error('propertyId and programId mandatory');
    }
    return this.API.patch(environment.API_NAME, '/properties/' + propertyId + '/to-program/' + programId, {});
  }

  public getItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      throw new Error('collection and id mandatory');
    }
    return this.API.get(environment.API_NAME, '/v2/collections/' + collection + '/' + id, {});
  }

  public getCurrency(programId: string | undefined): Promise<string> {
    if (!programId) {
      throw new Error('collection and id mandatory');
    }

    return this.API.get(environment.API_NAME, `/mobile/currency/${programId}`, {});
  }

  public getHistory(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      throw new Error('collection and id mandatory');
    }
    return this.API.get(environment.API_NAME, '/v2/history/' + collection + '/' + id, {});
  }

  public async fullTextSearchForm(searchParams: SearchParams): Promise<any> {
    return this.API.get(
      environment.API_NAME,
      `/v2/forms/search?collection=${searchParams.collection}&text=${encodeURIComponent(
        searchParams.searchText,
      )}&language=${searchParams.language}&path=${searchParams.path ?? ''}&programId=${searchParams.programId ?? ''}`,
      {},
    );
  }

  public async getHistoryVersion(collectionName: string, metadataId: string, version: number): Promise<any> {
    if (!collectionName || !metadataId) {
      throw new Error('collection and metadataId mandatory');
    }
    return this.API.get(environment.API_NAME, `/v2/history/${collectionName}/${metadataId}/${version}`, {});
  }

  public async getOnlineOfflineFormDefinitionItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      throw new Error('collection and id mandatory');
    }
    if (this.onLine) {
      const result = await this.API.get(environment.API_NAME, '/v2/collections/' + collection + '/' + id, {});
      this.configurationService.populateFormDefinitions([result]).catch((error) => this.logger.error(error));
      return result;
    } else {
      return this.configurationService.getFormDefinition(id);
    }
  }

  public async getOnlineOfflineDynamicFormItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      throw new Error('collection and id mandatory');
    }
    if (this.onLine) {
      const result = await this.API.get(environment.API_NAME, '/v2/collections/' + collection + '/' + id, {});
      this.dataService.populateDynamicForms([result], collection);
      return result;
    } else {
      return this.dataService.getDynamicForm(id);
    }
  }

  public async getOnlineOfflineProperties(filters: Array<any>): Promise<Array<Property>> {
    if (!filters || filters.length === 0) {
      throw new Error('filters mandatory');
    }
    if (this.onLine) {
      return await this.getCollection({ collection: 'properties', filters, excludeMetadata: false });
    } else {
      return this.dataService.getPropertiesByFieldValue(filters[0].field, filters[0].value, 0, 10000);
    }
  }

  public getSequence(formCollectionName: string): Promise<number> {
    if (!formCollectionName) {
      throw new Error('formCollectionName mandatory');
    }
    return this.API.get(environment.API_NAME, `/v2/sequence/${formCollectionName}`, {});
  }

  public markNotificationsAsRead(email: string | undefined): Promise<any> {
    if (!email) {
      throw new Error('email mandatory');
    }
    return this.API.patch(environment.API_NAME, `/notification/read/${email}`, {});
  }

  public translateText(text: string, target: string): Promise<string> {
    return this.API.post(environment.API_NAME, `/v2/translate/${target}`, { body: text });
  }

  public generateAndSendPdf(
    to: Array<string>,
    subject: string,
    message: string,
    footerMessage: string,
    customMessage: string,
    collection: string,
    formId: string,
    language: string,
    contactSummary: boolean,
    from: string,
  ): Promise<string> {
    const body = {
      to,
      subject,
      message,
      footerMessage,
      customMessage,
      collection,
      formId,
      language,
      contactSummary,
      from,
    };
    return this.API.post(environment.API_NAME, `/v2/email/send`, { body });
  }

  public async filterExistingItems(collection: string, ids: string[]): Promise<ExistingFormValidation> {
    const body: ExistingFormValidation = {
      key: collection,
      value: ids,
    };

    return this.API.post(environment.API_NAME, `/v2/forms/validate`, { body });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveCollectionItem(collection: string, item: any): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    this.logger.debug('saveCollectionItem', clone);
    return this.API.put(environment.API_NAME, '/v2/collections/' + collection, { body: clone });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public saveFormItem(formCollection: string, item: any): Promise<any> {
    if (!formCollection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    return this.API.put(environment.API_NAME, '/v2/forms/' + formCollection, { body: { form: clone } });
  }

  public generateLandOwnerTasks(landOwnerId: string, year: number): Promise<void> {
    return this.API.post(environment.API_NAME, '/forms/tasks/' + landOwnerId + '/' + year, {});
  }

  public saveOnlineOfflineFormItem(
    formCollection: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
  ): Promise<any> {
    if (!formCollection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('formCollection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    if (this.onLine) {
      return this.API.put(environment.API_NAME, '/v2/forms/' + formCollection, { body: { form: clone } });
    } else {
      return this.dataService.saveDynamicForm({
        body: { content: clone, collection: formCollection, filesToUpload, filesToDelete },
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public async saveProductiveModel(formCollection: string, item: any, systemInstances: Array<any>): Promise<any> {
    if (!formCollection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('formCollection and item mandatory');
    }
    if (!this.onLine) {
      throw new Error('Cannot saveProductiveModel when offline');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    const systemInstanceParams = [];
    for (const systemInstance of systemInstances) {
      systemInstanceParams.push({
        action: systemInstance.toDelete ? 'delete' : systemInstance._id ? 'update' : 'create',
        systemInstance: systemInstance,
      });
    }
    let updatedProductiveModel = clone;
    if (!systemInstances?.length) {
      updatedProductiveModel = await this.API.put(environment.API_NAME, '/forms/productive-models/' + clone._id, {
        body: {
          productiveModel: updatedProductiveModel,
          systemInstances: [],
        },
      });
    }
    for (let i = 0; i < systemInstanceParams.length; i += 10) {
      updatedProductiveModel = await this.API.put(environment.API_NAME, '/forms/productive-models/' + clone._id, {
        body: {
          productiveModel: updatedProductiveModel,
          systemInstances: systemInstanceParams.slice(i, i + 10),
        },
      });
    }

    return updatedProductiveModel;
  }

  public async fetchSystemInstances(productiveModelId: string): Promise<Array<any>> {
    if (this.onLine) {
      const systemInstances = [];
      let hasFetchedAll = false;
      let iteration = 0;
      const filters = [
        {
          field: 'formProductiveModelId',
          value: productiveModelId,
          operator: 'eq',
        },
      ];
      try {
        while (!hasFetchedAll) {
          const instances = await this.getCollection({
            collection: 'system-instances',
            sort: '_id',
            filters: filters,
            limit: 10,
            skip: iteration * 10,
            verifyAssociations: false,
            excludeMetadata: true,
          });
          systemInstances.push(...instances);
          if (instances.length === 0) {
            hasFetchedAll = true;
          }
          iteration++;
        }
        this.dataService.populateSystemInstances(systemInstances);
      } catch (error) {
        this.logger.error(error, false);
      }
      return systemInstances;
    } else {
      return await this.dataService.getSystemInstancesByFieldValue('formProductiveModelId', productiveModelId, 0, 200);
    }
  }

  public updateAllAssociatedProductiveModels(systemCatalogId: string): Promise<void> {
    if (!systemCatalogId) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('systemCatalogId mandatory');
    }
    if (!this.onLine) {
      throw new Error('Cannot updateAllRelatedProductiveModels when offline');
    }
    return this.API.patch(environment.API_NAME, '/forms/productive-models/all/' + systemCatalogId, {});
  }

  public async saveOnlineOfflineProductiveModel(
    formCollection: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
    systemInstances: Array<any>,
  ): Promise<any> {
    if (!formCollection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('formCollection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    for (const systemInstance of systemInstances) {
      if (!systemInstance.guidId) {
        systemInstance.guidId = uuidv4();
      }
    }
    if (this.onLine) {
      return this.saveProductiveModel(formCollection, item, systemInstances);
    } else {
      clone.systemInstances = structuredClone(systemInstances);
      this.dataService.populateSystemInstances(systemInstances);
      return this.dataService.saveDynamicForm({
        body: { content: clone, collection: formCollection, filesToUpload, filesToDelete },
      });
    }
  }

  public updateOnlineOfflineContact(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
  ): Promise<any> {
    if (!item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('item mandatory');
    }
    const clone = structuredClone(item);
    if (this.onLine) {
      return this.API.patch(environment.API_NAME, '/v2/contacts/' + item._id, { body: clone });
    } else {
      return this.dataService.saveContact({
        body: { content: clone, filesToUpload, filesToDelete },
      });
    }
  }

  public updateCollectionItem(collection: string, item: { _id: string }): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    this.logger.debug('updateCollectionItem', item);
    return this.API.patch(environment.API_NAME, `/v2/collections/${collection}/${item._id}`, { body: item });
  }

  public deleteCollectionItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and id mandatory');
    }
    this.logger.debug('deleteCollectionItem', id);
    return this.API.del(environment.API_NAME, `/v2/collections/${collection}/${id}`, {});
  }

  public deleteFormItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      throw new Error('collection and id mandatory');
    }
    return this.API.del(environment.API_NAME, `/v2/forms/${collection}/${id}`, {});
  }

  public deleteOnlineOfflineFormItem(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and id mandatory');
    }
    this.logger.debug('deleteOnlineOfflineFormItem', id);
    if (this.onLine) {
      return this.API.del(environment.API_NAME, `/v2/forms/${collection}/${id}`, {});
    } else {
      return this.dataService.archiveDynamicForm(collection, id);
    }
  }

  public deleteOnlineOfflineProductiveModel(collection: string, id: string): Promise<any> {
    if (!collection || !id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and id mandatory');
    }
    this.logger.debug('deleteOnlineOfflineFormItem', id);
    if (this.onLine) {
      return this.API.del(environment.API_NAME, `/forms/productive-models/${id}`, {});
    } else {
      return this.dataService.archiveDynamicForm(collection, id);
    }
  }

  public deleteOnlineOfflineContact(id: string): Promise<any> {
    if (!id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('id mandatory');
    }
    this.logger.debug('deleteOnlineOfflineContact', id);
    if (this.onLine) {
      return this.API.del(environment.API_NAME, `/v2/contacts/${id}`, {});
    } else {
      return this.dataService.archiveContact(id);
    }
  }

  public runOCR(ocrEndpoint: string, s3FileKeys: string[]): Promise<any> {
    return this.API.post(environment.API_NAME, `/v2/${ocrEndpoint}`, {
      body: s3FileKeys,
    });
  }

  public copyShapeToBaseMap(featureId: string, currentGeoserverWorkspace: string): Promise<any> {
    if (!featureId) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('featureId mandatory');
    }

    return this.API.post(environment.API_NAME, `/maps/gis/feature/${featureId}/copy-to-basemap`, {
      body: {
        geoserverWorkspace: currentGeoserverWorkspace,
      },
    });
  }

  public async postLocalProposition(proposition: CurrentProposition): Promise<CurrentProposition> {
    if (!proposition.id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('featureId mandatory');
    }

    return this.API.post(environment.API_NAME, `/maps/gis/local-proposition`, {
      body: proposition,
    });
  }

  public async getLocalPropositions(): Promise<CurrentProposition[]> {
    return this.API.get(environment.API_NAME, `/maps/gis/local-proposition/me`, {});
  }

  public async getLocalProposition(propositionId: string): Promise<CurrentProposition | null> {
    try {
      return await this.API.get(environment.API_NAME, `/maps/gis/local-proposition/id/${propositionId}`, {});
    } catch (error) {
      if ((error as any).$metadata.httpStatusCode === 404) {
        return null;
      }
      throw error;
    }
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public createCollectionItem(collection: string, item: any): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    delete clone._id;
    this.logger.debug('createCollectionItem', clone);
    return this.API.post(environment.API_NAME, '/v2/collections/' + collection, { body: clone });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public createFormItem(collection: string, item: any): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    delete clone._id;
    this.logger.debug('createFormItem', clone);
    return this.API.post(environment.API_NAME, '/v2/forms/' + collection, { body: { form: clone } });
  }

  public createOnlineOfflineFormItem(
    collection: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
  ): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    delete clone._id;
    if (this.onLine) {
      return this.API.post(environment.API_NAME, '/v2/forms/' + collection, { body: { form: clone } });
    } else {
      return this.dataService.createDynamicForm({
        body: { content: clone, collection, filesToUpload, filesToDelete },
      });
    }
  }

  public async createProductiveModel(
    collection: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    systemInstances: Array<any>,
  ): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    if (!this.onLine) {
      throw new Error('Cannot createProductiveModel when offline');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    delete clone._id;
    const systemInstanceParams = [];
    for (const systemInstance of systemInstances) {
      systemInstanceParams.push({
        action: systemInstance.toDelete ? 'delete' : 'create',
        systemInstance: systemInstance,
      });
    }
    if (systemInstanceParams.length <= 10) {
      return await this.API.post(environment.API_NAME, '/forms/productive-models', {
        body: {
          productiveModel: clone,
          systemInstances: systemInstanceParams,
        },
      });
    }
    let productiveModel = clone;
    for (let i = 0; i < systemInstanceParams.length; i += 10) {
      productiveModel = await this.API.put(environment.API_NAME, '/forms/productive-models/' + productiveModel._id, {
        body: {
          productiveModel: productiveModel,
          systemInstances: systemInstanceParams.slice(i, i + 10),
        },
      });
    }

    return productiveModel;
  }

  public createOnlineOfflineProductiveModel(
    collection: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    item: any,
    filesToUpload: Array<FileToUpload>,
    filesToDelete: Array<FileToDelete>,
    systemInstances: Array<any>,
  ): Promise<any> {
    if (!collection || !item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('collection and item mandatory');
    }
    const clone = structuredClone(item);
    delete clone.imagePath;
    delete clone._id;
    for (const systemInstance of systemInstances) {
      if (!systemInstance.guidId) {
        systemInstance.guidId = uuidv4();
      }
    }
    if (this.onLine) {
      return this.createProductiveModel(collection, item, systemInstances);
    } else {
      clone.systemInstances = systemInstances;
      this.dataService.populateSystemInstances(systemInstances);
      return this.dataService.createDynamicForm({
        body: { content: clone, collection, filesToUpload, filesToDelete },
      });
    }
  }
  public createFormDefinition(item: FormDefinition): Promise<FormDefinition> {
    if (!item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('item mandatory');
    }
    const clone: any = structuredClone(item);
    delete clone._id;

    this.logger.debug('createFormDefinition', clone);
    return this.API.post(environment.API_NAME, '/v2/form-definition', { body: clone });
  }

  public saveFormDefinition(item: FormDefinition): Promise<FormDefinition> {
    if (!item) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('item mandatory');
    }
    this.logger.debug('saveFormDefinition', item);
    return this.API.put(environment.API_NAME, '/v2/form-definition', { body: item });
  }

  public deleteFormDefinition(id: string): Promise<any> {
    if (!id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('id mandatory');
    }
    this.logger.debug('archiveFormDefinition', id);
    return this.API.del(environment.API_NAME, `/v2/form-definition/${id}`, {});
  }

  public archiveNews(id: string, news: Partial<News>): Promise<any> {
    if (!id) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('id mandatory');
    }
    this.logger.debug('archiveNews', id);
    return this.API.patch(environment.API_NAME, `/v2/news/${id}/archive`, { body: news });
  }

  public createMessage(message: unknown): Promise<any> {
    if (!message) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('message mandatory');
    }
    return this.API.post(environment.API_NAME, '/v2/messages', { body: message });
  }

  public createOwnerMessage(message: unknown): Promise<any> {
    if (!message) {
      this.logger.error(new Error('mandatory parameters error'));
      throw new Error('message mandatory');
    }

    return this.API.post(environment.API_NAME, '/v2/owner-messages', { body: message });
  }

  public publishFile(selectedFile: NzUploadFile, bundleObjectId: string): Promise<any> {
    return this.API.post(environment.API_NAME, `/v2/media-bundles/file-publish`, {
      body: {
        ...selectedFile.response,
        bundleObjectId: bundleObjectId,
        fileUid: selectedFile.uid,
      },
    });
  }

  public generatePdf(
    template: string,
    documentId: string,
    collectionName: string,
    language: string,
    currency: string,
    generationMode: string,
    contactSummary: boolean,
    mandateDate?: string,
  ): Promise<any> {
    let path = `/v2/generator/${template}?id=${documentId}&collectionName=${collectionName}&language=${language}&currency=${currency}&generationMode=${generationMode}&contactSummary=${contactSummary}&ts=${new Date().getTime()}`;

    if (mandateDate) {
      path += `&mandateDate=${mandateDate}`;
    }

    return this.API.get(environment.API_NAME, path, {});
  }

  public sendFormToOwner(
    collectionName: string,
    body: {
      formContentId: string;
      language: string;
      currency: string | null;
    },
  ): Promise<any> {
    return this.API.post(environment.API_NAME, `/forms/send-to-owner/${collectionName}`, { body: body });
  }

  public formDefinitionFullTextSearch(searchedText: string): Promise<FormDefinitionFullTextResult[]> {
    return this.API.get(environment.API_NAME, `/v2/form-definitions/full-text-search/${searchedText}`, {});
  }

  public searchContacts(searchedText: string): Promise<Contact[]> {
    if (this.onLine) {
      return this.API.get(environment.API_NAME, `/v2/contacts/search?searchedText=${searchedText}`, {});
    } else {
      return this.dataService.searchContactsByValue(searchedText);
    }
  }
}
